import { useTranslation } from "react-i18next";
import { SettingsFormContent, SettingsFormCTAs } from "./index";
import { Grid } from "@mantine/core";
import { CreateCompanyFormInput, CreateCompanyValidationSchema } from "./validation/departmentsValidationSchema";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Companies, Company, CreateAccount, CreateAddress, CreateCompany } from "@/types";
import { markAddressAsDefault } from "@/utils/AddressHelper";
import { useGetCompanyTypes, useGetCountries, useGetLanguages, useGetTimeZones } from "@/services/RegisterCompany";
import { useAuth } from "@/contexts/AuthContext";
import { useGetCreateOrderValues } from "@/services/Orders";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postCreateDepartment, useGetDepartmentsByParentCompanyId } from "@/services/Company";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import CheckBox from "@/components/atoms/Checkbox";
import Heading from "@/components/atoms/Heading";
import Stack from "@/components/atoms/Stack";
import TextInput from "@/components/atoms/TextInput";
import CompanyTypeEnum from "@/constants/enums/CompanyTypeEnum";
import Divider from "@/components/atoms/Divider";
import PhoneField from "@/components/atoms/PhoneInput/PhoneInput";
import DepartmentAddressTable from "../../organisms/SettingsTables/DepartmentAddressTable";
import DepartmentBankAccountTable from "../../organisms/SettingsTables/DepartmentBankAccountTable";
import CTA from "@/components/atoms/CTA";
import Text from "@/components/atoms/Text";
import SettingsTable from "../../molecules/SettingsTable/SettingsTable";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";

const DepartmentSettings = () => {
  const { t } = useTranslation();
  const { currentCompany, isAdminUser } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = isAdminUser(currentCompany);

  const { data: companyTypes } = useGetCompanyTypes();
  const { data: languages } = useGetLanguages();
  const { data: orderValues } = useGetCreateOrderValues();
  const { data: timeZones } = useGetTimeZones();
  const { data: countryOptions } = useGetCountries(1, {
    staleTime: 1000 * 60 * 5,
  });
  const { data: departments } = useGetDepartmentsByParentCompanyId(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });

  const { mutate: createDepartmentCompany } = useMutation({
    mutationFn: async (data: CreateCompany) => {
      await postCreateDepartment(data, currentCompany?.companyId as number);
    },
    onMutate: async (data: CreateCompany) => {
      const queryKey = ["getDepartmentsByParentCompanyId", currentCompany?.companyId];
      await queryClient.cancelQueries({ queryKey });
      const snapshots = queryClient.getQueriesData<Companies>({ queryKey });
      const optimisticCompany = { id: -Date.now(), companyName: data.name } as Company;
      queryClient.setQueriesData<Companies>({ queryKey }, (old) =>
        old ? { ...old, companies: [...old.companies, optimisticCompany] } : old
      );
      return { snapshots };
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:settings.createDepartmentSuccess"))
      );
      reset();
    },
    onError: (_err, _data, context) => {
      context?.snapshots?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:settings.createDepartmentError"))
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["getDepartmentsByParentCompanyId", currentCompany?.companyId],
      });
    },
  });

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
  } = useForm<CreateCompanyFormInput>({
    resolver: zodResolver(CreateCompanyValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      companyType: CompanyTypeEnum.DEPARTMENT,
      phone: "",
      languageId: 1,
      transport: false,
      allowCollectiveInvoice: false,
      address: [],
      bankAccount: [],
      membership: "STANDARD",
    },
  });

  const onSubmit = (data: CreateCompanyFormInput) => {
    const departmentObj: CreateCompany = {
      ...data,
      partialCompanyId: 0,
      isLocked: false,
      creditlimit: 0,
      parentCompanyId: currentCompany?.companyId as number,
      defaultCurrency: data.defaultCurrency.toUpperCase(),
      invoiceEmail: data.invoiceEmail || "",
      notificationEmail: data.notificationEmail || "",
      vatNumber: data.vatNumber || "",
      leiNumber: data.leiNumber || "",
      eoriNumber: data.eoriNumber || "",
      timeZone: data.timeZone || "",
      exportLicense: data.exportLicense || "",
    };
    createDepartmentCompany(departmentObj);
  };

  const addDepartmentCompanyAddress = (address: CreateAddress) => {
    setValue("address", [...getValues("address"), address]);
  };

  const removeDepartmentCompanyAddress = (address: CreateAddress) => {
    const updatedAddresses = getValues("address").filter((addr) => addr !== address);
    setValue("address", updatedAddresses);
  };

  const setDefaultDepartmentCompanyAddress = (address: CreateAddress) => {
    setValue("address", markAddressAsDefault(getValues("address"), address));
  };

  const addDepartmentCompanyAccount = (account: CreateAccount) => {
    setValue("bankAccount", [...getValues("bankAccount"), account]);
  };

  const removeDepartmentCompanyAccount = (account: CreateAccount) => {
    const updatedAccounts = getValues("bankAccount").filter((acc) => acc !== account);
    setValue("bankAccount", updatedAccounts);
  };

  return (
    <>
      <Stack>
        <Heading data-testid={"department-settings-heading"} order={2} styleOrder={4} tt="uppercase">
          {t("settings:departmentSettings.newDepartment")}
        </Heading>

        <SettingsFormContent>
          <form>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6, lg: 8 }}>
                <Controller
                  control={control}
                  name="name"
                  render={({ field, fieldState }) => (
                    <TextInput
                      {...field}
                      id="name"
                      required
                      data-testid={"name-input"}
                      label={t("common:companyName")}
                      placeholder={`${t("common:companyName")}...`}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Controller
                  control={control}
                  name="companyType"
                  render={({ field, fieldState }) => {
                    return (
                      <Select
                        {...field}
                        id="companyType"
                        label={t("common:companyType")}
                        disabled
                        data={transformDataToSelectOption(companyTypes || [], "", "", t, "enumerables:companyTypes")}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Controller
                  control={control}
                  name="vatNumber"
                  render={({ field, fieldState }) => {
                    return (
                      <TextInput
                        {...field}
                        id="vatNumber"
                        required
                        data-testid={"vatNumber-input"}
                        label={t("common:vatNumber")}
                        value={field.value || ""}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Controller
                  control={control}
                  name="leiNumber"
                  render={({ field, fieldState }) => {
                    return (
                      <TextInput
                        {...field}
                        id="leiNumber"
                        label={t("common:leiNumber")}
                        value={field.value || ""}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Controller
                  control={control}
                  name="eoriNumber"
                  render={({ field, fieldState }) => {
                    return (
                      <TextInput
                        {...field}
                        id="eoriNumber"
                        label={t("registerCompany:companyDetails.eoriNumber")}
                        value={field.value || ""}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>

              <Grid.Col span={12}>
                <Divider />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 6 }}>
                <Controller
                  control={control}
                  name="email"
                  render={({ field, fieldState }) => {
                    return (
                      <TextInput
                        {...field}
                        id="email"
                        required
                        data-testid={"email-input"}
                        label={t("common:email")}
                        value={field.value || ""}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 6 }}>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field, fieldState }) => {
                    return (
                      <PhoneField
                        countryCodeEditable={false}
                        value={field.value.toString() || ""}
                        onChange={(value) => field.onChange(value)}
                        testId={"phone-field"}
                        placeholder={t("common:phone")}
                        errormessage={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="invoiceEmail"
                  render={({ field, fieldState }) => {
                    return (
                      <TextInput
                        {...field}
                        id="invoiceEmail"
                        required={true}
                        data-testid={"invoice-email"}
                        label={t("common:invoiceemail")}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="notificationEmail"
                  render={({ field, fieldState }) => {
                    return (
                      <TextInput
                        {...field}
                        id="notificationEmail"
                        required={true}
                        label={t("settings:notificationEmails")}
                        data-testid={"notification-email"}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <Divider />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Controller
                  control={control}
                  name="languageId"
                  render={({ field, fieldState }) => {
                    return (
                      <Select
                        {...field}
                        id="language"
                        required
                        data-testid={"language-select"}
                        label={t("common:language")}
                        data={transformDataToSelectOption(
                          languages || [],
                          "id",
                          "languageCode",
                          t,
                          "enumerables:languages"
                        )}
                        onChange={(value) => {
                          field.onChange(Number(value));
                        }}
                        value={field.value.toString() || ""}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Controller
                  control={control}
                  name="timeZone"
                  render={({ field, fieldState }) => {
                    return (
                      <Select
                        {...field}
                        id="timeZone"
                        required
                        data-testid={"timezone-select"}
                        label={t("common:timeZone")}
                        data={transformDataToSelectOption(timeZones || [], "", "", t)}
                        value={field.value || ""}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Controller
                  control={control}
                  name="defaultCurrency"
                  render={({ field, fieldState }) => {
                    return (
                      <Select
                        {...field}
                        id="defaultCurrency"
                        required
                        data-testid={"currency-select"}
                        label={t("common:currency")}
                        data={transformDataToSelectOption(
                          orderValues?.currencies || [],
                          "value",
                          "value",
                          t,
                          undefined,
                          true
                        )}
                        value={field.value?.toLowerCase() || ""}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Controller
                  control={control}
                  name="countryCodeIso3"
                  render={({ field, fieldState }) => {
                    return (
                      <Select
                        {...field}
                        id="countryCodeIso3"
                        data-testid={"country-select"}
                        required
                        label={t("common:country")}
                        data={transformDataToSelectOption(countryOptions || [], "iso3", "name", t)}
                        value={field.value || ""}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>

              <Grid.Col span={12}>
                <Divider />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Controller
                  control={control}
                  name="exportLicense"
                  render={({ field, fieldState }) => {
                    return (
                      <TextInput
                        {...field}
                        id="exportLicense"
                        label={t("common:exportlicense")}
                        value={field.value || ""}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack>
                  <Controller
                    control={control}
                    name="transport"
                    render={({ field, fieldState }) => {
                      return (
                        <CheckBox
                          {...field}
                          id="handle-transport"
                          label={t("settings:handleTransport")}
                          checked={field.value}
                          value={field.value.toString()}
                          onChange={(e) => field.onChange(e.target.checked)}
                          error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                        />
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name="allowCollectiveInvoice"
                    render={({ field, fieldState }) => {
                      return (
                        <CheckBox
                          {...field}
                          id="allowCollectiveInvoice"
                          label={t("common:allowCollectiveInvoice")}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          value={field.value.toString()}
                          error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                        />
                      );
                    }}
                  />
                </Stack>
              </Grid.Col>
              <Grid.Col span={12}>
                <Divider />
              </Grid.Col>
            </Grid>
          </form>

          <Grid>
            <Grid.Col span={12}>
              <DepartmentAddressTable
                isAdmin={isAdmin}
                currentCompanyId={currentCompany?.companyId}
                departmentAddresses={watch("address") || []}
                addCompanyAddress={addDepartmentCompanyAddress}
                removeCompanyAddress={removeDepartmentCompanyAddress}
                setDefaultCompanyAddress={setDefaultDepartmentCompanyAddress}
              />
              <Text color="error" size="sm">
                {errors.address?.message ? t(errors.address.message) : undefined}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider />
            </Grid.Col>
            <Grid.Col span={12}>
              <DepartmentBankAccountTable
                bankAccounts={watch("bankAccount") || []}
                currentCompanyId={currentCompany?.companyId}
                isAdmin={isAdmin}
                addCompanyAccount={addDepartmentCompanyAccount}
                removeCompanyAccount={removeDepartmentCompanyAccount}
              />
              <Text color="error" size="sm">
                {errors.bankAccount?.message ? t(errors.bankAccount.message) : undefined}
              </Text>
            </Grid.Col>
          </Grid>

          <SettingsFormCTAs>
            <CTA
              id="save"
              size="lg"
              type="submit"
              onClick={handleSubmit(onSubmit)}
              disabled={currentCompany?.isDepartment ?? false}
              data-testid={"submit-btn"}
            >
              {t("settings:departmentSettings.createDepartment")}
            </CTA>
          </SettingsFormCTAs>
        </SettingsFormContent>

        <Heading order={2} styleOrder={4} tt="uppercase">
          {t("settings:navigation.departments")}
        </Heading>
        <SettingsFormContent>
          <DepartmentTable departments={departments?.companies || []} isAdmin={isAdmin} />
        </SettingsFormContent>
      </Stack>
    </>
  );
};

export default DepartmentSettings;

interface DepartmentTableProps {
  departments: Company[] | [];
  isAdmin: boolean;
}
const DepartmentTable = ({ departments, isAdmin }: DepartmentTableProps) => {
  const { t } = useTranslation();
  const { changeCompany } = useAuth();

  const columns: ColumnDef<Company>[] = useMemo(
    () => [
      {
        header: t("common:name"),
        accessorKey: "companyName",
      },
      {
        header: "",
        accessorKey: "id",
        cell: ({ row }) => {
          const onClick = async () => {
            await changeCompany(row.original.id);
          };
          return <CTA onClick={onClick}>{t("settings:departmentSettings.goToCompany")}</CTA>;
        },
      },
    ],
    [t]
  );

  return <SettingsTable data={departments} columns={columns} isAdmin={isAdmin} />;
};
